import CloudOffRoundedIcon from '@mui/icons-material/CloudOffRounded';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import { Button, Paper, Stack, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import { isAfter, isBefore, parseISO } from 'date-fns';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';

import RouteLoader from '@sophocles/components/App/RouteLoader';
import SystemNoticeBanner from '@sophocles/components/SystemNotice/SystemNoticeBanner';
import { fetchActiveSystemBanner } from '@sophocles/entities/SystemBanner';
import { fetchAuthUser } from '@sophocles/features/auth/authActions';
import { getAuthUserSelector, isAuthenticatedSelector } from '@sophocles/features/auth/authSelectors';
import checkCredentials, { sendHome } from '@sophocles/router/checkCredentials';
import store from '@sophocles/store/redux-store';

/**
 * Result a guard (``onEnter``) returns:
 *
 * - ``true``        — allow rendering
 * - ``false``       — deny: user genuinely lacks access
 * - ``'transient'`` — could not determine right now (network blip,
 *                    rehydrate race, etc.). RequireAuth retries before
 *                    surfacing a "couldn't verify" UI.
 *
 * Returning ``true`` / ``false`` is back-compat with existing guards;
 * ``'transient'`` is the new opt-in that prevents iOS-resume cold-boot
 * races from bouncing the user off a deep route.
 */
export type GuardResult = boolean | 'transient';

interface RequireAuthProps {
  component: React.ComponentType<any>;
  componentProps?: Record<string, any>;
  onEnter?: (
    props: { params: Record<string, string | undefined>; location: ReturnType<typeof useLocation> },
    navigate: ReturnType<typeof useNavigate>,
  ) => Promise<GuardResult> | GuardResult;
}

// Retry tuning. Cold-boot iOS resume races can take a couple seconds to
// settle while the auth record / customer / sitegroup caches warm up.
// Three retries with backoff (~3.6s total) covers the common cases. On
// each retry we also force a fresh /users/me/ refresh so a stale
// rehydrated user record (e.g. is_staff missing) gets corrected before
// the guard runs again.
const MAX_AUTOMATIC_RETRIES = 3;
const RETRY_DELAY_MS = 1200;

type Phase = 'pending' | 'allow' | 'deny' | 'transient';

const RequireAuth: React.FC<RequireAuthProps> = ({ component: Component, componentProps = {}, onEnter }) => {
  // Each route has its own `<RequireAuth>` element, so navigating
  // between routes remounts this component. Defaulting `phase` to
  // `'pending'` would flash the shell skeleton on every click. Read
  // auth + user synchronously from the store at mount: if the user
  // is already authenticated (the typical intra-app navigation),
  // render the route immediately and re-validate the guard in the
  // background. The evaluation effect below will flip to `'deny'` or
  // `'transient'` if the guard rejects.
  const initialAuthed = isAuthenticatedSelector(store.getState());
  const initialUser = initialAuthed ? getAuthUserSelector(store.getState()) : null;
  const [phase, setPhase] = useState<Phase>(initialAuthed ? 'allow' : 'pending');
  const [currentUser, setCurrentUser] = useState<null | any>(initialUser);
  // Subscribe to the auth slice so we react to revocation. If a 401
  // interceptor (or explicit logout elsewhere) flips
  // isAuthenticated → false while we're rendered, the optimistic
  // 'allow' phase below redirects to /login on the next render
  // instead of leaving the user staring at a stale page.
  const isAuthenticated = useSelector(isAuthenticatedSelector);
  const [notice, setNotice] = useState<null | any>(null);
  const [attempt, setAttempt] = useState(0);
  const { t } = useTranslation();

  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();

  const props = { params, location };
  const match = {
    params,
    isExact: location.pathname === window.location.pathname,
    path: window.location.pathname,
    url: window.location.href,
  };

  // Reset attempt counter whenever the route or guard changes; the
  // user is asking for something new.
  const lastKeyRef = useRef('');
  useEffect(() => {
    const key = `${location.pathname}${location.search}`;
    if (key !== lastKeyRef.current) {
      lastKeyRef.current = key;
      setAttempt(0);
    }
  }, [location.pathname, location.search]);

  useEffect(() => {
    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;

    const evaluate = async () => {
      // 1. Auth must settle before any guard runs. checkCredentials
      //    rehydrates from token if needed.
      const user = await checkCredentials();
      if (cancelled) return;

      setCurrentUser(user);

      if (!user) {
        // Render path will redirect to /login.
        setPhase('deny');
        return;
      }

      // On retries (attempt > 0) force a fresh /users/me/ fetch.
      // Cold-boot rehydrate can leave the auth user record stale (e.g.
      // missing is_staff or up-to-date group memberships), which
      // makes deep-route guards return false on the first try even
      // though the user actually has access.
      if (attempt > 0) {
        try {
          await store.dispatch(fetchAuthUser() as any);
        } catch {
          // ignore — guard will re-evaluate either way
        }
        if (cancelled) return;
      }

      // 2. Guard.
      let result: GuardResult;
      try {
        if (typeof onEnter === 'function') {
          const r = await onEnter(props, navigate);
          // Coerce undefined/null to false; pass strings ('transient') through.
          if (r === 'transient') result = 'transient';
          else result = !!r;
        } else {
          result = true;
        }
      } catch {
        // Treat unexpected errors as transient so a flaky network
        // doesn't permanently kick the user off a valid route.
        result = 'transient';
      }
      if (cancelled) return;

      if (result === true) {
        // Guard passed — flip to allow before the banner fetch so the
        // page paints immediately; the banner is best-effort.
        if (!cancelled) setPhase('allow');
        try {
          const systemBanner = await fetchActiveSystemBanner();
          if (!cancelled) setNotice(systemBanner && !systemBanner.error ? systemBanner : null);
        } catch {
          // ignore
        }
        return;
      }

      if (result === 'transient' && attempt < MAX_AUTOMATIC_RETRIES) {
        retryTimer = setTimeout(() => {
          if (!cancelled) setAttempt(a => a + 1);
        }, RETRY_DELAY_MS);
        return;
      }

      setPhase(result === 'transient' ? 'transient' : 'deny');
    };

    evaluate();

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [onEnter, location.pathname, location.search, navigate, attempt]);

  const closeNotice = () => setNotice(null);

  // Auth was revoked underneath us (token expired, 401 interceptor,
  // explicit logout) *while we were already rendering the route*.
  // Redirect immediately rather than letting the optimistic 'allow'
  // phase keep showing a stale page. Scoped to phase==='allow' so a
  // cold-boot refresh — where the Redux auth slice is briefly false
  // before checkCredentials() rehydrates from the persisted token —
  // shows the shell loader instead of bouncing to /login.
  if (phase === 'allow' && !isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (phase === 'pending') {
    return <RouteLoader showShell />;
  }

  if (!currentUser) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (phase === 'transient' || phase === 'deny') {
    // Single friendly UI for any reason we couldn't render the page —
    // network blips, cold-boot races, or genuine permission gaps. We
    // intentionally don't tell the user *why*: cold-boot races look
    // identical to permanent denials from here, and surfacing a
    // confident "Not authorized" message when the user actually does
    // have access (and just needs the auth/store to settle) is worse
    // than a soft "couldn't load right now."
    const handleRetry = () => {
      setPhase('pending');
      setAttempt(a => a + 1);
    };
    return (
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
          paddingTop: 'env(safe-area-inset-top)',
          paddingBottom: 'env(safe-area-inset-bottom)',
          overflow: 'hidden',
          bgcolor: theme => theme.palette.background.default,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            maxWidth: 420,
            width: '100%',
            p: { xs: 3, sm: 4 },
            borderRadius: 3,
            textAlign: 'center',
            border: theme => `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box
            sx={{
              width: 72,
              height: 72,
              mx: 'auto',
              mb: 2,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: theme => theme.palette.action.hover,
              color: 'text.secondary',
            }}
          >
            <CloudOffRoundedIcon sx={{ fontSize: 40 }} />
          </Box>
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
            {t('auth.couldNotLoadTitle', {
              defaultValue: "We couldn't load this page",
            })}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t('auth.couldNotLoadBody', {
              defaultValue: 'Give it a moment and try again, or head back to home.',
            })}
          </Typography>
          <Stack direction={{ xs: 'column-reverse', sm: 'row' }} spacing={1} justifyContent="center">
            <Button
              variant="outlined"
              startIcon={<HomeRoundedIcon />}
              onClick={() => sendHome(navigate)}
              fullWidth
              sx={{ maxWidth: { sm: 160 } }}
            >
              {t('auth.goHome', { defaultValue: 'Go Home' })}
            </Button>
            <Button
              variant="contained"
              startIcon={<RefreshRoundedIcon />}
              onClick={handleRetry}
              fullWidth
              sx={{ maxWidth: { sm: 160 } }}
            >
              {t('actions.tryAgain', { defaultValue: 'Try Again' })}
            </Button>
          </Stack>
        </Paper>
      </Box>
    );
  }

  // phase === 'allow'
  const isStaff = currentUser?.get?.('is_staff', false) ?? (currentUser as any)?.is_staff ?? false;
  const showNotice =
    notice &&
    notice.enabled &&
    (!notice.staff_only || isStaff) &&
    isBefore(new Date(), parseISO(notice.end_time)) &&
    isAfter(new Date(), parseISO(notice.start_time));

  return (
    <Component
      {...componentProps}
      match={match}
      router={{ location, navigate }}
      location={location}
      notice={showNotice ? <SystemNoticeBanner close={closeNotice} html={notice.html} /> : null}
    />
  );
};

export default RequireAuth;
